import React, { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DIALOG_TYPE } from '../../../../constants';
import { XIcon } from '../../../../icons/animated';
import styles from './Dialog.module.css';
import { DialogActionButtons } from './DialogActionButtons';

interface IDialog {
  isOpen: boolean;
  onClose: () => void;
  handleApply: () => void;
  title: string;
  children: ReactNode;
  dialogType?: DIALOG_TYPE;
  justify?: 'start' | 'center' | 'end';
  confirmButtonLable: string;
  cancelButtonLable?: string;
  disabled?: boolean;
  maxWidth?: string; // e.g. '900px', '80vw'
  noScroll?: boolean; // remove content max-height / overflow
  closeOnOutsideClick?: boolean;
}

const dialogStyles: Record<NonNullable<IDialog['dialogType']>, string> = {
  info: 'border-blue-500',
  warning: 'border-yellow-500',
  success: 'border-green-500',
  error: 'border-red-500',
};

export const Dialog = (props: IDialog) => {
  const {
    isOpen,
    onClose,
    title,
    children,
    dialogType = DIALOG_TYPE.INFO,
    handleApply,
    justify = 'end',
    cancelButtonLable,
    confirmButtonLable,
    disabled,
    maxWidth = '700px',
    noScroll = false,
    closeOnOutsideClick = true,
  } = props;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (closeOnOutsideClick) {
      onClose();
    }
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return createPortal(
    <div
        className='fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-md transition-all duration-500'
        onClick={handleOverlayClick}
    >
      <div
          className={`w-[95%] rounded-2xl shadow-2xl bg-white/95 backdrop-blur-sm p-6 ${styles.dialogAnimation} border border-gray-200`}
          onClick={handleContentClick}
          style={{
            maxWidth,
            boxShadow:
            'rgba(0, 0, 0, 0.08) 0px 20px 40px -12px, rgba(0, 0, 0, 0.05) 0px 8px 16px -8px',
          }}
      >
        <div className='flex items-center justify-between mb-4'>
          <h2 className='text-xl font-medium text-gray-900'>{ title }</h2>
          <button
              type='button'
              onClick={onClose}
              className='h-8 w-8 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 flex items-center justify-center transition-colors'
              aria-label='Close dialog'
          >
            <XIcon size={16} />
          </button>
        </div>
        <div
            className={`text-gray-600 text-sm leading-relaxed mb-8 ${
              noScroll
                ? ''
                : `max-h-[60vh] overflow-y-auto ${styles.customScrollbar}`
            }`}
        >
          { children }
        </div>

        <DialogActionButtons
            handleApply={handleApply}
            onClose={onClose}
            dialogType={dialogType}
            justify={justify}
            actionButton={confirmButtonLable}
            cancelButton={cancelButtonLable}
            disabled={disabled}
        />
      </div>
    </div>,
    document.body,
  );
};
